import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect, useRef } from 'react';
import { Copy, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { useSupportToken } from '@/hooks/useSupportToken';
import { formatTimeRemaining, getTimeRemaining, copyToClipboard, isTokenExpired } from '@/utils/supportToken';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface SupportTokenModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SupportTokenModal({ open, onOpenChange }: SupportTokenModalProps) {
  useLanguage();
  const { token, isLoading, error, generateToken, revokeToken } = useSupportToken();
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isExpired, setIsExpired] = useState(false);
  const copyButtonRef = useRef<HTMLButtonElement>(null);

  // Auto-generate token when modal opens if no valid token exists
  useEffect(() => {
    if (open && !token && !isLoading && !error) {
      generateToken();
    }
  }, [open, token, isLoading, error, generateToken]);

  // Update timer every second
  useEffect(() => {
    if (!token || token.status !== 'active') {
      setTimeRemaining(0);
      return;
    }

    const updateTimer = () => {
      const remaining = getTimeRemaining(token.expiresAt);
      setTimeRemaining(remaining);

      if (remaining <= 0 && !isExpired) {
        setIsExpired(true);
      }
    };

    // Initial update
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [token, isExpired]);

  // Reset expired state when token changes
  useEffect(() => {
    if (token && token.status === 'active' && !isTokenExpired(token.expiresAt)) {
      setIsExpired(false);
    }
  }, [token]);

  // Focus on copy button when token is ready
  useEffect(() => {
    if (open && token && token.status === 'active' && !isExpired && copyButtonRef.current) {
      setTimeout(() => {
        copyButtonRef.current?.focus();
      }, 100);
    }
  }, [open, token, isExpired]);

  const handleCopy = async () => {
    if (!token) return;

    const success = await copyToClipboard(token.token);
    if (success) {
      toast.success(translateInterfaceText("Copiado!"), {
        description: translateInterfaceText("Token copiado para a área de transferência."),
      });
    } else {
      toast.error(translateInterfaceText("Erro ao copiar"), {
        description: translateInterfaceText("Não foi possível copiar o token."),
      });
    }
  };

  const renderContent = () => {
    // Loading state
    if (isLoading && !token) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p className="text-sm text-muted-foreground">{translateInterfaceText("Gerando token...")}</p>
        </div>
      );
    }

    // Error state
    if (error) {
      let errorMessage = translateInterfaceText("Não foi possível gerar o token. Tentar novamente.");
      if (error === 'offline') {
        errorMessage = 'Você está offline. Verifique sua conexão.';
      } else if (error === 'rate_limit') {
        errorMessage = 'Você gerou tokens demais hoje. Tente novamente em 1 hora.';
      }

      return (
        <div className="flex flex-col items-center justify-center py-12">
          <p className="text-sm text-muted-foreground mb-4 text-center">{errorMessage}</p>
          <Button onClick={generateToken} disabled={isLoading}>
            {translateInterfaceText("Tentar novamente")}</Button>
        </div>
      );
    }

    // No token or revoked state
    if (!token || token.status === 'revoked') {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <p className="text-sm text-muted-foreground mb-4">{translateInterfaceText("Nenhum token ativo.")}</p>
          <Button onClick={generateToken} disabled={isLoading}>
            {translateInterfaceText("Gerar novo token")}</Button>
        </div>
      );
    }

    // Expired state
    if (isExpired || token.status === 'expired' || isTokenExpired(token.expiresAt)) {
      return (
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-destructive/10 text-destructive text-sm font-medium">
            {translateInterfaceText("Expirado")}</div>
          <p className="text-sm text-muted-foreground">{translateInterfaceText("Este token não é mais válido.")}</p>
          <Button onClick={generateToken} disabled={isLoading}>
            {translateInterfaceText("Gerar novo token")}</Button>
        </div>
      );
    }

    // Active token state
    return (
      <div className="space-y-6">
        {/* Token display */}
        <div className="flex flex-col items-center space-y-3 py-6 px-4 bg-muted/50 rounded-xl">
          <div 
            className="text-2xl md:text-3xl font-mono font-bold tracking-wider text-foreground select-all"
            aria-live="polite"
          >
            {token.token}
          </div>
          
          {/* Timer */}
          <div className="text-sm text-muted-foreground" aria-live="polite">
            {translateInterfaceText("Válido por:")}{" "}<span className="font-semibold tabular-nums">{formatTimeRemaining(timeRemaining)}</span>
          </div>
        </div>

        {/* Action button */}
        <Button
          ref={copyButtonRef}
          onClick={handleCopy}
          className="w-full"
          disabled={isLoading}
        >
          <Copy className="h-4 w-4 mr-2" />
          {translateInterfaceText("Copiar token")}</Button>

        {/* Security note */}
        <p className="text-xs text-muted-foreground text-center leading-relaxed">
          {translateInterfaceText("O token é de uso único, expira em 30 minutos e não dá acesso ao seu e-mail ou senha.")}</p>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{translateInterfaceText("Token de suporte")}</DialogTitle>
          <DialogDescription>
            {translateInterfaceText("Informe este token ao atendente para autorizar o acesso temporário à sua conta.")}</DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {renderContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
}
